import { Fragment, useState, useEffect, useRef } from 'react';
import Paginator from 'react-hooks-paginator';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/seo';
import LayoutOne from '../../layouts/LayoutOne';
import Breadcrumb from '../../wrappers/breadcrumb/Breadcrumb';
import ShopSidebar from '../../wrappers/product/ShopSidebar';
import ShopTopbar from '../../wrappers/product/ShopTopbar';
import ShopProducts from '../../wrappers/product/ShopProducts';
import {
  getCartInfo,
  getClientProducts,
  productSelector,
} from '../../store/slices/product-slice';
import OrderContextInfo, { OrderCategoryInfo } from '../../components/OrderContextInfo';
import {
  authSelector,
  setSelectedCategories,
  setOrderCategoryId,
} from '../../store/slices/auth-slice';
import { Spinner } from 'react-bootstrap';
import cogoToast from 'cogo-toast';

const Products = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {t} =  useTranslation();
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search);
  const seasonalCategoryId = query?.get('category');
  const [clientInfo, setClientInfo] = useState(null);
  const { selectedCategories, orderCategoryId } = useSelector(authSelector);
  // The cart stays bound to its category until the explicit reset (client change), even when emptied:
  // the backend refuses any other category, so redirect back instead of listing an empty page
  const isCategoryBlocked =
    !!orderCategoryId &&
    !!seasonalCategoryId &&
    orderCategoryId !== seasonalCategoryId;
  const { isLoading } = useSelector(productSelector);
  const [layout, setLayout] = useState('grid three-column');
  const [sortType, setSortType] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [filterSortType, setFilterSortType] = useState('');
  const [filterSortValue, setFilterSortValue] = useState('');
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [sortedProducts, setSortedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageLimit, setPageLimit] = useState(9);
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedCategoryLabels, setSelectedCategoryLabels] = useState([]);
  const [sortOrder, setSortOrder] = useState('');
  const [allFabrics, setAllFabrics] = useState([]);
  const [allCategoryLabels, setAllCategoryLabels] = useState([]);
  const [seasonLine, setSeasonLine] = useState(null);
  const [cartInfo, setCartInfo] = useState(null);

  const getLayout = (layout) => {
    setLayout(layout);
  };

  const getFilterSortParams = (sortType, sortValue) => {
    setFilterSortType(sortType);
    setFilterSortValue(sortValue);
  };

  const handlePageLimitChange = (newPageLimit) => {
    setPageLimit(newPageLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleFabricChange = (fabrics) => {
    setSelectedFabrics(fabrics);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleCategoryLabelChange = (categoryLabels) => {
    setSelectedCategoryLabels(categoryLabels);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleSearch = (term) => {
    setCurrentPage(1);
    setOffset(0);
    setSearchTerm(term);
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
    setOffset(0);
  };

  useEffect(() => {
    // Quando cambia categoria via query string, riparti dalla prima pagina
    setCurrentPage(1);
    setOffset(0);
  }, [seasonalCategoryId]);

  const getProductFabricCode = (product) => product?.FabricCode || '';
  const getProductCategoryLabel = (product) => product?.Category || '';

  const applySort = (list, order) => {
    if (!order) return list;
    const sorted = [...list];
    switch (order) {
      case 'name_asc':
        return sorted.sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
      case 'name_desc':
        return sorted.sort((a, b) => (b.productName || '').localeCompare(a.productName || ''));
      case 'price_asc':
        return sorted.sort((a, b) => (a.productPrice || 0) - (b.productPrice || 0));
      case 'price_desc':
        return sorted.sort((a, b) => (b.productPrice || 0) - (a.productPrice || 0));
      case 'fabric_asc':
        return sorted.sort((a, b) =>
          getProductFabricCode(a).localeCompare(getProductFabricCode(b))
        );
      default:
        return list;
    }
  };

  const needsFullCatalog =
    searchTerm.trim() !== '' ||
    selectedFabrics.length > 0 ||
    selectedCategoryLabels.length > 0 ||
    sortOrder !== '';

  // Ref (non uno state) perché non deve innescare un altro giro di effetto:
  // serve solo a ricordare per quale categoria abbiamo già popolato i filtri,
  // per non rilanciare quella fetch ad ogni cambio pagina/ricerca/ordinamento.
  const filtersPopulatedForCategoryRef = useRef(null);

  // Le due fetch di getClientProducts (popolamento filtri + lista prodotti)
  // devono girare in sequenza, mai in parallelo: condividono lo stesso cart
  // lato server (stesso token), e due richieste concorrenti possono corrompere
  // le strutture interne del ShoppingCart lato OFBiz.
  useEffect(() => {
    if (!seasonalCategoryId || isCategoryBlocked) {
      setAllFabrics([]);
      setAllCategoryLabels([]);
      setProducts([]);
      setTotalRecords(0);
      return;
    }

    let cancelled = false;

    (async () => {
      if (filtersPopulatedForCategoryRef.current !== seasonalCategoryId) {
        filtersPopulatedForCategoryRef.current = seasonalCategoryId;
        await dispatch(
          getClientProducts({
            setProducts: (all) => {
              const countMap = {};
              all.forEach((p) => {
                const code = getProductFabricCode(p);
                if (code) countMap[code] = (countMap[code] || 0) + 1;
              });
              setAllFabrics(
                Object.entries(countMap)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([code, count]) => ({ code, count }))
              );

              const categoryCountMap = {};
              all.forEach((p) => {
                const category = getProductCategoryLabel(p);
                if (category) {
                  categoryCountMap[category] = (categoryCountMap[category] || 0) + 1;
                }
              });
              setAllCategoryLabels(
                Object.entries(categoryCountMap)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([code, count]) => ({ code, count }))
              );
            },
            seasonalCategoryId,
            setTotalRecords: () => {},
            setResponseInfo: (info) => {
              setClientInfo(info);
            },
            viewIndex: 0,
            viewSize: 9999,
          })
        );
        if (cancelled) return;
        // After the first listing (it binds a new cart to its category), still in sequence with the cart calls
        await dispatch(getCartInfo({ setCartInfo }));
      }

      if (cancelled) return;

      // Quando ricerca o filtri client-side sono attivi, carica l'intero catalogo dal WS
      if (needsFullCatalog) {
        await dispatch(
          getClientProducts({
            setProducts,
            seasonalCategoryId,
            setTotalRecords,
            setSeasonLine,
            viewIndex: 0,
            viewSize: 9999,
          })
        );
      } else {
        await dispatch(
          getClientProducts({
            setProducts,
            seasonalCategoryId,
            setTotalRecords,
            setSeasonLine,
            viewIndex: currentPage - 1,
            viewSize: pageLimit,
          })
        );
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCategories, seasonalCategoryId, pageLimit, searchTerm, selectedFabrics, selectedCategoryLabels, sortOrder, isCategoryBlocked]);

  useEffect(() => {
    if (!seasonalCategoryId) {
      navigate('/categories');
      return;
    }

    // Vincolo singola categoria: il carrello resta legato alla sua categoria (anche se vuoto) fino al Nuovo Ordine
    if (isCategoryBlocked) {
      cogoToast.error(
        t('The cart is bound to another category. Complete the order or start a New Order to change category.'),
        { position: 'top-right', hideAfter: 6 }
      );
      navigate(`/products?category=${orderCategoryId}`, { replace: true });
      return;
    }

    // Registra la categoria corrente come categoria dell'ordine attivo
    dispatch(setOrderCategoryId(seasonalCategoryId));
  }, [seasonalCategoryId, isCategoryBlocked, orderCategoryId, t, navigate, dispatch]);

  useEffect(() => {
    return () => {
      console.log('Unmount');
      dispatch(setSelectedCategories([]));
    };
  }, [dispatch]);

  //   useEffect(() => {
  //     let sortedProducts = getSortedProducts(products, sortType, sortValue);
  //     const filterSortedProducts = getSortedProducts(
  //       sortedProducts,
  //       filterSortType,
  //       filterSortValue
  //     );
  //     sortedProducts = filterSortedProducts;
  //     setSortedProducts(sortedProducts);
  //     setCurrentData(sortedProducts.slice(offset, offset + pageLimit));
  //   }, [offset, products, sortType, sortValue, filterSortType, filterSortValue]);

  return (
    <Fragment>
      <SEO titleTemplate={t("Products")} description={t("Products page.")} />

      <LayoutOne headerTop="visible">
        {/* breadcrumb */}
        <Breadcrumb
  leftContent={
    clientInfo && (
      <div className="client-info">
        <div>
          {t("Customer")}: <strong>{clientInfo.partyName}</strong>
        </div>
        {cartInfo?.orderCategoryId && (
          <div>
            <OrderCategoryInfo cartInfo={cartInfo} />
          </div>
        )}
        {seasonLine && (
          <div>
            {t("Line")}: <strong>{seasonLine}</strong>
          </div>
        )}
      </div>
    )
  }
  rightContent={<OrderContextInfo cartInfo={cartInfo} />}
  pages={[
    { label: t("Home"), path: process.env.PUBLIC_URL + "/" },
    { label: t("Products"), path: process.env.PUBLIC_URL + pathname }
  ]}
/>

        <div className="shop-area pt-95 pb-100">
          <div className="container-fluid products-order-container">
            <div className="row">
              <div className="col-lg-3 col-xl-2 order-1 order-lg-1 mb-30 mb-lg-0">
                {/* shop sidebar */}
                <ShopSidebar
                  sideSpaceClass="mr-30"
                  onSearch={handleSearch}
                  fabrics={allFabrics}
                  selectedFabrics={selectedFabrics}
                  onFabricChange={handleFabricChange}
                  categoryLabels={allCategoryLabels}
                  selectedCategoryLabels={selectedCategoryLabels}
                  onCategoryLabelChange={handleCategoryLabelChange}
                />
              </div>
              <div className="col-lg-9 col-xl-10 order-2 order-lg-2">
                {isLoading ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      minHeight: '100vh',
                    }}
                  >
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <ShopTopbar
                      currentPage={currentPage}
                      getLayout={getLayout}
                      getFilterSortParams={getFilterSortParams}
                      onPageLimitChange={handlePageLimitChange}
                      onSortChange={handleSortChange}
                      pageLimit={pageLimit}
                      productCount={totalRecords}
                      sortedProductCount={products.length}
                      sortOrder={sortOrder}
                    />

                    {/* shop page content default */}
                    {(() => {
                      // Calcola la lista da mostrare e il totale per il Paginator
                      let displayProducts;
                      let paginatorTotal;

                      if (needsFullCatalog) {
                        // Filtri + ordinamento client-side sull'intero catalogo
                        const filtered = applySort(
                          products
                            .filter((p) => {
                              if (!searchTerm.trim()) return true;
                              const term = searchTerm.toLowerCase();
                              return (
                                (p.productName || p.internalName || '')
                                  .toLowerCase()
                                  .includes(term) ||
                                (p.SKU || '').toLowerCase().includes(term)
                              );
                            })
                            .filter((p) =>
                              selectedFabrics.length === 0 ||
                              selectedFabrics.includes(getProductFabricCode(p))
                            )
                            .filter((p) =>
                              selectedCategoryLabels.length === 0 ||
                              selectedCategoryLabels.includes(getProductCategoryLabel(p))
                            ),
                          sortOrder
                        );
                        paginatorTotal = filtered.length;
                        // Paginazione client-side
                        displayProducts = filtered.slice(
                          (currentPage - 1) * pageLimit,
                          currentPage * pageLimit
                        );
                      } else {
                        // Paginazione server-side: products è già la pagina corrente
                        displayProducts = products;
                        paginatorTotal = totalRecords;
                      }

                      return (
                        <>
                          <ShopProducts layout={layout} products={displayProducts} />

                          {/* shop product pagination */}
                          <div className="pro-pagination-style text-center mt-30">
                            <Paginator
                              totalRecords={paginatorTotal}
                              pageLimit={pageLimit}
                              pageNeighbours={2}
                              setOffset={setOffset}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              pageContainerClass="mb-0 mt-0"
                              pagePrevText="«"
                              pageNextText="»"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

export default Products;
